import React, { FC, ReactElement } from 'react';
import { FormikValues, FormikActions } from 'formik';
import { animateScroll } from 'react-scroll';

import AccreditedInvestorStatus from '@buyside/components/presentation/AccreditedInvestorProfilePage/Pages/accreditedInvestorStatus/AccreditedInvestorStatus';
import FinancialBackground from '@buyside/components/presentation/AccreditedInvestorProfilePage/Pages/financialBackground/FinancialBackground';
import PreviousInvestments from '@buyside/components/presentation/AccreditedInvestorProfilePage/Pages/previousInvestments/previousInvestments';
import AccreditedInvestorSuccessPage from '@buyside/components/presentation/AccreditedInvestorProfilePage/Pages/accreditedInvestorSuccess/accreditedInvestorSuccessPage';
import Wizard from '@buyside/components/presentation/Form/Wizard/Wizard';
import { ideasUrl } from '@buyside/util/urlHelper';

import {
  investorStatusSchema,
  financialBackgroundSchema,
  previousInvestmentsSchema
} from '@buyside/components/presentation/AccreditedInvestorProfilePage/Helpers/validations';

import {
  CurrentUserAccreditedInvestorProfileQuery,
  CreateOrUpdateAccreditedInvestorProfileMutationFn
} from '@interfaces/graphql-typings';
type AccreditedProfileCurrentUser = NonNullable<
  CurrentUserAccreditedInvestorProfileQuery['currentUser']
>;

const AccreditedInvestorProfilePage: FC<{
  accreditedInvestorProfile?: AccreditedProfileCurrentUser['accreditedInvestorProfile'];
  createOrUpdateAccreditedInvestorProfile: CreateOrUpdateAccreditedInvestorProfileMutationFn;
  isLoading: boolean;
  userId: AccreditedProfileCurrentUser['id'];
}> = ({
  accreditedInvestorProfile,
  createOrUpdateAccreditedInvestorProfile,
  isLoading,
  userId
}): ReactElement => {
  const initialValues = accreditedInvestorProfile
    ? { ...accreditedInvestorProfile, incomeReq: null, netWorthReq: null, userId: userId }
    : {
        bonds: null,
        foreignCurrencies: null,
        futuresOrCommodities: null,
        hedgeFunds: null,
        incomeReq: null,
        investmentSize: null,
        netWorth: null,
        netWorthReq: null,
        next12MoAllocation: null,
        privateCompanies: null,
        privateEquityFunds: null,
        publiclyTradedStocks: null,
        realEstate: null,
        userId,
        yearsInvesting: null
      };

  const handleErrors = (bag: FormikActions<FormikValues>): void => {
    bag.setSubmitting(false);
  };

  const handleSubmit = async (
    values: FormikValues,
    bag: FormikActions<FormikValues>,
    next: (values: FormikValues) => void
  ): Promise<void> => {
    if (Object.values(values).includes(null)) {
      const untouchedFields = Object.keys(values).reduce(
        (acc: { [field: string]: boolean }, key) => {
          acc[key] = false;
          return acc;
        },
        {}
      );

      bag.setTouched(untouchedFields);
    }

    const {
      bonds,
      foreignCurrencies,
      futuresOrCommodities,
      hedgeFunds,
      incomeReq,
      investmentSize,
      netWorth,
      netWorthReq,
      next12MoAllocation,
      privateCompanies,
      privateEquityFunds,
      publiclyTradedStocks,
      realEstate,
      yearsInvesting
    } = values;

    createOrUpdateAccreditedInvestorProfile({
      variables: {
        userId,
        bonds,
        foreignCurrencies,
        futuresOrCommodities,
        hedgeFunds,
        incomeReq,
        investmentSize,
        netWorth,
        netWorthReq,
        next12MoAllocation,
        privateCompanies,
        privateEquityFunds,
        publiclyTradedStocks,
        realEstate,
        yearsInvesting
      }
    })
      .then((value): void => {
        const response = value?.data?.createOrUpdateAccreditedInvestorProfile;

        if (response?.unaccredited) {
          return window.location.assign(ideasUrl());
        }

        if (response?.accreditedInvestorProfile) {
          bag.setSubmitting(false);
          next(values);
          return animateScroll.scrollToTop();
        }
      })
      .catch((): void => {
        handleErrors(bag);
      });
  };
  return (
    <Wizard initialValues={initialValues} onSubmit={handleSubmit}>
      <AccreditedInvestorStatus isLoading={isLoading} validationSchema={investorStatusSchema} />
      <FinancialBackground isLoading={isLoading} validationSchema={financialBackgroundSchema} />
      <PreviousInvestments isLoading={isLoading} validationSchema={previousInvestmentsSchema} />
      <AccreditedInvestorSuccessPage userId={userId} />
    </Wizard>
  );
};

export default AccreditedInvestorProfilePage;
